import { For, Setter, Show, Signal, createEffect, createMemo, createSignal, onMount } from "solid-js"
import { createStore, produce } from "solid-js/store"
import { extractDataEditor as api } from 'st-app/api'
import STInput from "../st-input/st-input"
import STCheckbox from "../st-checkbox/st-checkbox"
import { BOPropertyInfo, BusinessObjectInfo, ExtractData, ExtractDataDetail, ExtractDataItem, RelatedBusinessObjectResponse } from "st-app/api"
import './extract-data-editor.css'
import STModal from "../st-modal/st-modal"
import STButton, { ButtonType } from "../st-button/st-button"
import { ExtractDataEditorProvider, useExtractDataEditor } from "./extract-data-editor-context"
import Select from "../st-select/st-select"
import STCheckboxList, { Option } from "../st-checkbox-list/st-checkbox-list"
import ButtonGroup from "../st-button-group/st-button-group"
import { uuidv4 } from "common"

export class ExtractDataEditorProps {
  reportGu: string = ''
  events = {
    onSaved: () => {}
  }
}
export default function ExtractDataEditor(props: ExtractDataEditorProps) {
  const [store, setStore] = createStore<ExtractData>({
    ID: uuidv4(),
    locked: false,
    guid: '',
    label: '',
    namespace: '',
    primaryObject: '',
    resultType: 'NESTED',
    items: [],
    details: [],
  })

  const [initialState, setInitialState] = createSignal<ExtractData | null>(null)

  const findNode = (haystack: ExtractDataDetail[], needle: ExtractDataDetail): ExtractDataDetail | null => {
    for(const detail of haystack) {
      if (detail === needle) return detail
      
      const r = findNode(detail.details, needle)
      if (r) return r
    }

    return null
  }

  const addDetail = (parentId: string, detail: ExtractDataDetail) => {
    if (!parentId) return

    if (parentId === store.ID) {
      setStore('details', [...store.details, detail])
      return
    }

    const addDetailRecursive = (nodes: ExtractDataDetail[]): ExtractDataDetail[] => {
      return nodes.map((currentNode) => {
        if (currentNode.ID === parentId) {
          return {
            ...currentNode,
            details: [...currentNode.details, detail],
          }
        } else if (currentNode.details) {
          return {
            ...currentNode,
            details: addDetailRecursive(currentNode.details),
          }
        }
        return currentNode
      })
    }

    setStore('details', s => addDetailRecursive(s))
  }

  const removeDetail = (parentId: string, detail: ExtractDataDetail) => {
    if (!parentId) {
      removePrimaryObject()
      return
    }

    if (parentId === store.ID) {
      const idx = store.details.findIndex(d => d.ID === detail.ID)
      setStore('details', (d) => [...d.slice(0, idx), ...d.slice(idx + 1)])
      return
    }

    const removeDetailRecursive = (nodes: ExtractDataDetail[]): ExtractDataDetail[] => {
      return nodes.map((currentNode) => {
        if (currentNode.ID === parentId) {
          const details = currentNode.details
          const idx = details.findIndex(d => d.ID === detail.ID)
          return {
            ...currentNode,
            details: [...details.slice(0, idx), ...details.slice(idx + 1)],
          }
        } else if (currentNode.details) {
          return {
            ...currentNode,
            details: removeDetailRecursive(currentNode.details)
          }
        }
        return currentNode
      })
    }

    setStore('details', s => removeDetailRecursive(s))
  }
  
  const setPrimaryObject = (bo: BusinessObjectInfo) => {
    setStore((s: ExtractData) => ({
      ...s,
      label: bo.name ?? '',
      namespace: bo.nameSpace ?? '',
      primaryObject: bo.boid ?? '',
      items: [],
      details: [],
    } satisfies ExtractData))
  }


  const removePrimaryObject = () => {
    setStore(s => ({
      ...s,
      label: '',
      namespace: '',
      primaryObject: '',
      items: [],
      details: [],
    }))
  }

  const toggleItem = (detail: ExtractDataDetail, prop: BOPropertyInfo, value?: boolean) => {
    const items = detail.items
    const idx = items.findIndex(d => d.srcElement === prop.srcElement)
    const exists = idx >= 0

    const shouldAdd = value === undefined ? !exists : value === true
    const shouldRemove = value === undefined ? exists : value === false

    let newItems: ExtractDataItem[] = [...items]
    if (shouldAdd && !exists) {
      newItems = [...items, {
        srcElement: prop.srcElement,
        srcObject: prop.boid,
        type: prop.type,
        visible: true,
        showTitle: true,
        showAllOptions: false,
        columns: 0,
        labelWrap: false,
        locked: false,
        neverDisable: false,
        order: 0,
      }]
    } else if (shouldRemove && exists) {
      newItems = [
        ...items.slice(0, idx),
        ...items.slice(idx + 1),
      ]
    }

    if (detail.ID === store.ID) {
      setStore('items', newItems)
      return
    }

    const toggleItemRecursive = (nodes: ExtractDataDetail[]): ExtractDataDetail[] => {
      return nodes.map((currentNode) => {
        if (currentNode.ID === detail.ID) {
          return {
            ...detail,
            items: newItems
          }
        } else if (currentNode.details) {
          return {
            ...currentNode,
            details: toggleItemRecursive(currentNode.details)
          }
        }
        return currentNode
      })
    }

    setStore('details', d => toggleItemRecursive(d))
  }

  const [isSaving, setIsSaving] = createSignal(false)

  const save = async () => {
    setIsSaving(true)
    try {
      const updated = await api.SaveExtractData(store.guid!, store)
      setStore(updated)
      setInitialState(updated)
    } catch (e) {
      console.error(e)
    }
    setIsSaving(false)

    props.events.onSaved()

    if ((window as any).refreshDesigner) {
      (window as any).refreshDesigner()
    }
  }

  const saveAndClose = async () => {
    await save()

    if ((window as any).closeDataSourceEditor) {
      (window as any).closeDataSourceEditor()
    }
  }

  createEffect(async () => {
    if (props.reportGu) {
      const extractData = await api.LoadExtractData(props.reportGu);
      setStore(extractData)
      setInitialState(extractData)
    }
  })

  const isDirty = createMemo(() => {
    return didChange(initialState(), store)
  })

  const undo = () => {
    if (initialState()) {
      setStore(initialState()!)
    }
  }

  const undoAndClose = () => {
    undo()
    if ((window as any).closeDataSourceEditor){
      (window as any).closeDataSourceEditor();
    }
  }

  return (
    <div class="extract-data-editor">
      <ExtractDataEditorProvider>
        <div class="title-bar">
          <span>{store.fullyQualifiedName}</span>
          <div class="flexbox horizontal" style="gap: 4px;">
            <ButtonGroup>
              <STButton icon="fa fa-check" loading={isSaving()} enabled={isDirty()} type="success" onClick={save}>
                Save
              </STButton>
              <STButton enabled={!isSaving() && isDirty()} type="success" onClick={saveAndClose}>
                <span class="fa fa-times"></span>
              </STButton>
            </ButtonGroup>
            
            <ButtonGroup>
              <STButton icon="fa fa-share" enabled={!isSaving() && isDirty()} type="warning" onClick={undo}>
                Undo
              </STButton>
              <STButton enabled={!isSaving() && isDirty()} onClick={undoAndClose} type="warning">
                <span class="fa fa-times"></span>
              </STButton>
            </ButtonGroup>
          </div>
        </div>
        <MainInterface
          extractData={store}
          addDetail={addDetail}
          removeDetail={removeDetail}
          setPrimaryObject={setPrimaryObject}
          toggleItem={toggleItem}
        ></MainInterface>
        {/* <pre>{JSON.stringify(store, null, 2)}</pre> */}
      </ExtractDataEditorProvider>
    </div>
  )
}
ExtractDataEditor.defaultProps = new ExtractDataEditorProps()

function didChange (o: ExtractDataDetail | null, n: ExtractDataDetail) {
  if (!o) return false

  for (const nDetail of n.details) {
    const oDetail = o.details.find(od => od.primaryObject === nDetail.primaryObject)
    if (!oDetail) return true
    if (didChange(oDetail, nDetail)) return true 
  }

  for (const nItem of n.items) {
    const oItem = o.items.find(oi => oi.srcElement === nItem.srcElement)
    if (!oItem) return true
  }

  if (o.items.some(oItem => !n.items.some(nItem => oItem.srcElement === nItem.srcElement))) return true

  return false
}

interface MainInterfaceProps {
  extractData: ExtractData 
  toggleItem: (detail: ExtractDataDetail, prop: BOPropertyInfo, value?: boolean) => void,
  addDetail: (parentId: string, detail: ExtractDataDetail) => void,
  removeDetail: (parentId: string, detail: ExtractDataDetail) => void,
  setPrimaryObject: (bo: BusinessObjectInfo) => void,
}

function MainInterface(props: MainInterfaceProps) {
  const [editor, { loadBusinessObjects }] = useExtractDataEditor()!

  const [selectedDetailId, setSelectedDetailId] = createSignal('')
  const selectedDetail = createMemo(() => {
    const find = (detail: ExtractDataDetail): ExtractDataDetail | null => {
      if (detail.ID === selectedDetailId()) return detail
      for (const child of detail.details) {
        const found = find(child)
        if (found) return found
      }
      return null
    }
    return find(props.extractData)
  })

  const [selectPrimaryOpen, setSelectPrimaryOpen] = createSignal(false)

  const setPrimaryObject = (bo: BusinessObjectInfo) => {
    props.setPrimaryObject(bo)
    setSelectPrimaryOpen(false)
  }

  const toggleItem = (prop: BOPropertyInfo, value?: boolean) => {
    const detail = selectedDetail()
    if (detail) {
      props.toggleItem(detail, prop, value) 
    }
  }

  const items = createMemo(() => selectedDetail()?.items ?? [])

  const getBoids = (extract: ExtractDataDetail) => {
    const res: string[] = []
    res.push(extract.primaryObject!)
    res.push(...extract.details.flatMap(d => getBoids(d)))
    return res
  }

  return (
    <div class="main-interface">
      <div class="panel left-panel">
        <Show when={props.extractData.primaryObject}>
          <TreeNode
            selectedDetail={selectedDetail()}
            nodes={[props.extractData]}
            selectedDetailId={[selectedDetailId, setSelectedDetailId]}
            addDetail={props.addDetail}
            removeDetail={props.removeDetail}
          ></TreeNode>
        </Show>
        <Show when={!props.extractData.primaryObject}>
          <div style="width: 100%; height: 100%; display: flex; justify-content: center; align-items: center;">
            <STButton type="primary" onClick={() => setSelectPrimaryOpen(true)}>Select Primary Object</STButton>
          </div>
        </Show>
      </div>
      <div class="panel right-panel">
        <PropertyPicker
          detail={selectedDetail()}
          items={items()}
          toggleItem={toggleItem}
        ></PropertyPicker>
      </div>
      <STModal show={[selectPrimaryOpen, setSelectPrimaryOpen]}>
        <div class="business-object-picker-modal">
          <div class="business-object-picker-header">
            <span>Select a Business Object</span>
            <button class="fa fa-x" onClick={() => setSelectPrimaryOpen(false)}></button>
          </div>
          <Show when={selectPrimaryOpen()} fallback={<div></div>}>
            <BusinessObjectPicker selectedDetail={selectedDetail()} setSelectedBusinessObject={setPrimaryObject}></BusinessObjectPicker>
          </Show>

        </div>
      </STModal>
      {/* <pre>{JSON.stringify(foo[0](), null, 2)}</pre> */}
    </div>
  )
}

interface TreeNodeProps {
  parent?: ExtractDataDetail
  nodes: ExtractDataDetail[]
  selectedDetailId: Signal<string>
  selectedDetail: ExtractDataDetail | null
  addDetail: (parentId: string, detail: ExtractDataDetail) => void
  removeDetail: (parentId: string, detail: ExtractDataDetail) => void
}

function TreeNode(props: TreeNodeProps) {
  const [editor, { toggleAddOneToOneModal }] = useExtractDataEditor()!
  const [open, setOpen] = createSignal(true)
  const [addRelatedOpen, setAddRelatedOpen] = createSignal(false)
  const [selectedDetailId, setSelectedDetailId] = props.selectedDetailId

  const addRelatedBusinessObject = (bo: BusinessObjectInfo) => {
    const selectedDetail = props.selectedDetailId[0]()

    if (!selectedDetail) return;

    const newDetail: ExtractDataDetail = {
      ID: uuidv4(),
      locked: false,
      primaryObject: bo.boid ?? '',
      label: bo.name ?? '',
      items: [],
      details: [],
    }

    props.addDetail(selectedDetail, newDetail)

    setAddRelatedOpen(false)
    setSelectedDetailId(newDetail.ID!)
  }

  const removeDetail = (detail: ExtractDataDetail) => {
    const parentId = props.parent?.ID

    props.removeDetail(props.parent?.ID ?? '', detail)

    if (parentId) {
      setSelectedDetailId(parentId)
    }
  }

  const launchAdd = (detail: ExtractDataDetail) => {
    setSelectedDetailId(detail.ID!)
    // setAddRelatedOpen(true)
    toggleAddOneToOneModal(true)
  }
  


  return (
    <ul role="tree" class='extract-data-tree'>
      <For each={props.nodes}>{(detail) => (
        <li role="treeitem">
          <div class="extract-data-detail" classList={{ 'selected': detail.ID === selectedDetailId() }} onClick={() => setSelectedDetailId(detail.ID!)}>
            {/* <Show when={detail.details.length}> */}
              <span
                class="fa detail-node-toggle"
                classList={{ 'fa-chevron-right': !open(), 'fa-chevron-down': open(), }}
                style={{
                  opacity: detail.details.length ? '100%' : 0,
                }}
                onClick={() => setOpen(!open())}
              ></span>
            {/* </Show> */}
            <span class="detail-node-label">
              {detail.fullyQualifiedName}
            </span>
            <div class="detail-node-actions">
              <STButton type="primary" onClick={() => launchAdd(detail)}>
                <span class="fa fa-plus"></span>
              </STButton>

              <Show when={!detail.locked}>
                <STButton type="danger" onClick={() => removeDetail(detail)}>
                  <span class="fa fa-x"></span>
                </STButton>
              </Show>
            </div>
          </div>
          <Show when={detail.details.length && open()}>
            <TreeNode selectedDetail={props.selectedDetail} parent={detail} nodes={detail.details} selectedDetailId={props.selectedDetailId} addDetail={props.addDetail} removeDetail={props.removeDetail}></TreeNode>
          </Show>
        </li>
      )}</For>
      <STModal show={[addRelatedOpen, setAddRelatedOpen]}>
        <div class="business-object-picker-modal">
          <div class="business-object-picker-header">
            <span>Select a Business Object</span>
            <button class="fa fa-x" onClick={() => setAddRelatedOpen(false)}></button>
          </div>
          <Show when={addRelatedOpen()} fallback={<div></div>}>
            <BusinessObjectPicker selectedDetail={props.selectedDetail} setSelectedBusinessObject={(bo) => addRelatedBusinessObject(bo)}></BusinessObjectPicker>
          </Show>

        </div>
      </STModal>
    </ul>
  )
}

interface PropertyPickerProps {
  detail: ExtractDataDetail | null,
  items: ExtractDataItem[],
  toggleItem: (prop: BOPropertyInfo, value?: boolean) => void,
}

type FilterOption = 'Selected' | 'Currency' | 'Date' |
                    'DateTime' | 'GUID' | 'Image' |
                    'Lookup' | 'Memo' | 'Numeric' |
                    'Phone' | 'SSN' | 'String'

const filterOptions: Option[] = [
  { value: 'Selected', label: 'Selected' },
  { value: 'Currency', label: 'Currency' },
  { value: 'Date', label: 'Date' },
  { value: 'DateTime', label: 'DateTime' },
  { value: 'GUID', label: 'GUID' },
  { value: 'Image', label: 'Image' },
  { value: 'Lookup', label: 'Lookup' },
  { value: 'Memo', label: 'Memo' },
  { value: 'Numeric', label: 'Numeric' },
  { value: 'Phone', label: 'Phone' },
  { value: 'SSN', label: 'SSN' },
  { value: 'String', label: 'String' },
]

function PropertyPicker(props: PropertyPickerProps) {  
  const [editor, { setSelectedPropertyPickerBoid }] = useExtractDataEditor()!
  const [searchStr, setSearchStr] = createSignal('')
  const [findResults, setFindResults] = createSignal<BOPropertyInfo[]>([])

  createEffect(() => setSelectedPropertyPickerBoid(props.detail?.primaryObject ?? ''))

  const properties = createMemo(() => {
    const s = searchStr()
    return findResults()
      .filter(f => !searchStr() || f.srcElement?.includes(s))
      .filter(f => !filter().includes('Selected') || items().some(p => p.srcElement == f.srcElement && p.srcObject == f.boid))
      .filter(f => filter().includes(f.type as FilterOption))
  })

  const items = createMemo(() => props.detail?.items ?? [])

  createEffect(async (s) => {
    if (editor.propertyPicker.selectedBoid) {
      const result = editor.businessObjects[editor.propertyPicker.selectedBoid]?.properties ?? []

      setSearchStr('')
      if (result) {
        setFindResults(result)
      }
    } else {
      setFindResults([])
    }
  })

  const toggleItem = (property: BOPropertyInfo, value?: boolean) => {
    const isLocked = items().find(p => p.srcElement === property.srcElement && p.srcObject == property.boid)?.locked
    if (!isLocked) {
      props.toggleItem(property, value)
    }
  }

  const onKeydownItem = (event: KeyboardEvent, property: BOPropertyInfo) => {
    switch (event.code) {
      case 'Space':
      case 'Enter':
        event.preventDefault()
        toggleItem(property)
        break;
    }
  }

  const [filter, setFilter] = createSignal<FilterOption[]>([
    "Currency",
    "Date",
    "DateTime",
    "GUID",
    "Image",
    "Lookup",
    "Memo",
    "Numeric",
    "Phone",
    "SSN",
    "String",
  ])

  return (
    <div class='property-picker'>
      <Show when={props.detail}>
        <BoTabBar
          detail={props.detail!}
          toggleItem={toggleItem}
        ></BoTabBar>
      </Show>
      <div style="display: grid; grid-template-columns: 1fr auto; gap: 4px;">
        <STInput value={[searchStr, setSearchStr]} placeholder="Filter"></STInput>
        <div style="position: relative">
          <Select label="" icon="filter" value={createSignal('')}>
            <div style="min-width: 80px;">
              <STCheckboxList value={[filter, setFilter as Setter<string[]>]} options={filterOptions}></STCheckboxList>
            </div>
          </Select>
        </div>
      </div>

      <table class="property-list">
        <tbody>
          <For each={properties()}>{(property) => (
            <tr
              role="button"
              tabIndex={0}
              aria-pressed="false"
              onClick={() => toggleItem(property)}
              onKeyDown={(e) => onKeydownItem(e, property)}
              classList={{
                active: !!items().some(p => p.srcObject === property.boid && p.srcElement === property.srcElement),
                locked: items().find(p => p.srcObject === property.boid && p.srcElement === property.srcElement)?.locked,
              }}
            >
              <td class="property-title">
                <SearchText search={searchStr()} text={property.srcElement ?? ''}></SearchText>
              </td>
              <td class="property-type">{property.type}</td>
              <td>
                <Show when={items().some(p => p.srcObject === property.boid && p.srcElement === property.srcElement)}>
                  <Show
                    when={!items().find(p => p.srcObject === property.boid && p.srcElement === property.srcElement)?.locked}
                    fallback={<span class="fa fa-lock"></span>}
                  >
                    <span class="fa fa-check"></span>
                  </Show>
                  
                </Show>
              </td>
            </tr>
          )}</For>
        </tbody>
      </table>
    </div>
  )
}

interface TabBarProps {
  detail: ExtractDataDetail
  toggleItem: (item: BOPropertyInfo, value?: boolean) => void
}

interface BOTab {
  boid: string
  title: string
  locked: boolean
}

function BoTabBar(props: TabBarProps) {
  const [editor, {
    loadPropertyPickerBos,
    setSelectedPropertyPickerBoid,
    addPropertyPickerBo,
    removePropertyPickerBo,
    toggleAddOneToOneModal,
    resetAdditionalPropertyPickerBos,
  }] = useExtractDataEditor()!

  // const [selectedBoid, setSelectedBoid] = props.selectedBoid
  // const [businessObjects, setBusinessObjects] = createSignal<BusinessObjectInfo[]>([])
  // const [additionalBusinessObjects, setAdditionalBusinessObjects] = createSignal<BusinessObjectInfo[]>([])
  const [isAdding, setIsAdding] = createSignal<boolean>(false)

  createEffect(async () => {
    await loadPropertyPickerBos(props.detail)
    // const boids = [...new Set(props.detail.items.map(i => i.srcObject!))]
    // const boInfos = await loadBusinessObjects(boids)
    // setBusinessObjects(boInfos)
  })

  createEffect(() => {
    if (props.detail) {
      resetAdditionalPropertyPickerBos()
    }
  })

  createEffect(() => setIsAdding(editor.propertyPicker.isAdding))
  createEffect(() => {
    if (isAdding() != editor.propertyPicker.isAdding) {
      toggleAddOneToOneModal(isAdding())
    }
  })

  const tabs = createMemo<BOTab[]>(() => {
    const allBos = [...editor.propertyPicker.businessObjects, ...editor.propertyPicker.additionalBusinessObjects]
    return allBos.map(bo => ({
      boid: bo.boid!,
      title: `${bo.nameSpace}.${bo.name}`,
      locked: bo.boid === props.detail.primaryObject || props.detail.items.some(i => i.srcObject === bo.boid && i.locked),
    })) ?? []
  })

  const removeBo = (tab: BOTab) => {
    const bo = [...editor.propertyPicker.businessObjects, ...editor.propertyPicker.additionalBusinessObjects].find(b => b.boid === tab.boid)
    if (bo && !tab.locked) {
      for (const property of bo.properties) {
        props.toggleItem(property, false)
      }

      removePropertyPickerBo(bo)
    }
  }

  return (
    <div class="bo-tab-bar-container">
      <div class="bo-tab-bar">
        <For each={tabs()}>{(tab) => (
          <a
            href="#"
            class="bo-tab"
            classList={{ active: tab.boid === editor.propertyPicker.selectedBoid}}
            tabIndex={0}
            onClick={() => setSelectedPropertyPickerBoid(tab.boid)}
          >
            <span class="bo-tab-title">{tab.title}</span>

            <Show when={!tab.locked}>
              <div style="cursor: pointer;" onClick={() => removeBo(tab)}>
                <span class="fa fa-close"></span>
              </div>
            </Show>
          </a>
        )}</For>
      </div>
      <div class="add-new-container">
        <STButton onClick={() => {toggleAddOneToOneModal(true)}}>
          <span class="fa fa-plus"></span>
        </STButton>
        
        <STModal show={[isAdding, setIsAdding]}>
          <div class="business-object-picker-modal">
            <div class="business-object-picker-header">
              <span>Select a Business Object</span>
              <STButton type="info" onClick={() => toggleAddOneToOneModal(false)}>
                <span class="fa fa-x"></span>
              </STButton>
            </div>
            <Show when={props.detail} fallback={<div></div>}>
              <BusinessObjectPicker selectedDetail={props.detail} setSelectedBusinessObject={addPropertyPickerBo}></BusinessObjectPicker>
            </Show>
          </div>
        </STModal>
      </div>
    </div>
  )
}

interface BusinessObjectPickerProps {
  setSelectedBusinessObject: (bo: BusinessObjectInfo) => void
  selectedDetail: ExtractDataDetail | null
}

function BusinessObjectPicker(props: BusinessObjectPickerProps) {
  const [filter, setFilter] = createSignal('')
  const [allBOs, setAllBOs] = createSignal(false)

  const [findResults, setFindResults] = createSignal<RelatedBusinessObjectResponse>()


  createEffect(async () => {
    const results = await api.GetRelatedBusinessObjects(props.selectedDetail?.primaryObject ?? '', allBOs())
    setFindResults(results)
  })

  const filteredResults = createMemo(() => {
    if (!filter()) return findResults()

    const s = filter()

    const filtered: RelatedBusinessObjectResponse = {
      frequentlyUsed: findResults()?.frequentlyUsed.filter(f => `${f.nameSpace}.${f.name}`.toLowerCase().includes(s?.toLowerCase())) ?? [],
      primary: findResults()?.primary.filter(f => `${f.nameSpace}.${f.name}`.toLowerCase().includes(s?.toLowerCase())) ?? [],
      nonPrimary: findResults()?.nonPrimary.filter(f => `${f.nameSpace}.${f.name}`.toLowerCase().includes(s?.toLowerCase())) ?? [],
    }

    return filtered
  })

  return (
    <div class='business-object-picker'>
      <STInput value={[filter,setFilter]} placeholder="Filter"></STInput>
      <div class="results-list">
        <Show when={filteredResults()}>
          <Show when={filteredResults()?.frequentlyUsed.length}>
            <div class="group-header">Frequently Used</div>
            <For each={filteredResults()!.frequentlyUsed}>{(result) => (
              <button class="bo-find-result" onClick={() => props.setSelectedBusinessObject(result)}>
                <SearchText search={filter()} text={`${result.nameSpace}.${result.name}`}></SearchText>
              </button>
            )}</For>
          </Show>
          <Show when={filteredResults()!.primary.length}>
            <div class="group-header">Primary</div>
            <For each={filteredResults()!.primary}>{(result) => (
              <button class="bo-find-result" onClick={() => props.setSelectedBusinessObject(result)}>
                <SearchText search={filter()} text={`${result.nameSpace}.${result.name}`}></SearchText>
              </button>
            )}</For>
          </Show>
          <Show when={filteredResults()!.nonPrimary.length}>
            <div class="group-header">Non-Primary</div>
            <For each={filteredResults()!.nonPrimary}>{(result) => (
              <button class="bo-find-result" onClick={() => props.setSelectedBusinessObject(result)}>
                <SearchText search={filter()} text={`${result.nameSpace}.${result.name}`}></SearchText>
              </button>
            )}</For>
          </Show>
        </Show>
      </div>
      {/* <STCheckbox model={[allBOs, setAllBOs]} label="All business objects"></STCheckbox> */}
    </div>
  )
}

function SearchText(props: { text: string, search: string }) {
  const startIndex = createMemo(() => props.text.toLowerCase().indexOf(props.search.toLowerCase()))
  const endIndex = createMemo(() => startIndex() + props.search.length)

  const start = () => props.text.substring(0, startIndex())
  const match = () => props.text.substring(startIndex(), endIndex())
  const end = () => props.text.substring(endIndex())

  return (
    <>
      <span>{start()}</span>
      <span style='color: red;'>{match()}</span>
      <span>{end()}</span>
    </>
  )
}